// Registry de renderers del detalle de la bitácora — el ÚNICO punto de extensión.
//
// Resolución en cascada, siempre defensiva:
//   1. BY_EVENT_TYPE[event_type] si su guard canRender(detail) acepta el payload;
//   2. BY_SHAPE por la forma del JSON (cubre shapes históricos y eventos sin registrar);
//   3. GenericDetailTable como último recurso — nunca null, nunca JSON crudo.
//
// Descriptor: {Component, canRender(detail), getTitle?(entry, t), hasDetail?(detail)}
//   - Component null => el evento NO tiene panel (la fila no muestra "Ver detalle").
//   - getTitle puede devolver null => cae a la description del backend, limpiada.
// Registrar un evento nuevo = una entrada acá (+ keys i18n). El core no se toca.

import {isPlainObject, isAlertShape, isFlatDiffShape, isNestedDiffShape, isBeforeAfterShape, isFieldDiff} from './shapes';
import {HIDDEN_FIELDS, VALUE_LABELS, LEVEL_BADGES} from './labels';
import {GenericDetailTable, hasRenderableRows} from './primitives/GenericDetailTable';
import {AlertDetail} from './renderers/AlertDetail';
import {alertTitle, INDICATOR_LABELS} from './alert_indicators';
import {FlatDiffDetail, getFlatDiffTitle} from './renderers/FlatDiffDetail';
import {NestedDiffDetail, getNestedDiffTitle} from './renderers/NestedDiffDetail';
import {BudgetModifiedDetail} from './renderers/BudgetModifiedDetail';
import {DailyLimitDetail} from './renderers/DailyLimitDetail';
import {SimpleBeforeAfterDetail} from './renderers/SimpleBeforeAfterDetail';
import {
    PlatformChangeDetail, canRenderPlatformChange, platformChangeHasDetail,
} from './renderers/PlatformChangeDetail';
import {MediaPlanChangeDetail, canRenderMediaPlanChange} from './renderers/MediaPlanChangeDetail';
import {
    MemoryCurationBurstDetail,
    canRenderMemoryCurationBurst,
    memoryCurationBurstHasDetail,
    memoryCurationBurstTitle,
} from './renderers/MemoryCurationBurstDetail';
import {
    SimpleFieldsDetail,
    canRenderCommentPreview,
    canRenderSessionRename,
    canRenderWebhookSent,
    canRenderInvitedEmail,
    canRenderRemovedEmail,
    canRenderUncontrolledFlagged,
} from './renderers/SimpleFieldsDetail';

// Eventos sin panel de detalle: todo lo renderizable ya está en el título/fila.
export const NO_DETAIL = {Component: null, canRender: () => true, hasDetail: () => false};

// hasDetail por familia: ¿el renderer va a pintar ALGO? Un toggle "Ver detalle" que abre
// un panel vacío es un bug de UX — cada predicado espeja la lógica de descarte del
// renderer (campos ocultos, indicadores sin datos, changes sin diffs visibles).

function alertHasDetail(detail) {
    if (!isPlainObject(detail)) return false;
    if (LEVEL_BADGES[detail.level]) return true;
    return Object.entries(detail).some(([field, value]) =>
        !HIDDEN_FIELDS.has(field) && field !== 'indicator' && field !== 'level'
        && value !== null && value !== undefined && value !== '');
}

function flatDiffHasDetail(detail) {
    if (!isPlainObject(detail)) return false;
    return Object.entries(detail).some(([field, value]) => !HIDDEN_FIELDS.has(field) && isFieldDiff(value));
}

function nestedDiffHasDetail(detail) {
    if (!isPlainObject(detail)) return false;
    if (typeof detail.account_name === 'string' && detail.account_name.trim()) return true;
    if (typeof detail.edited_email === 'string' && detail.edited_email.trim()) return true;
    const changes = isPlainObject(detail.changes) ? detail.changes : {};
    return Object.entries(changes).some(([field, value]) => !HIDDEN_FIELDS.has(field) && isFieldDiff(value));
}

// Los 8 PLATFORM_* comparten renderer: el shape del mapper es el mismo, cambia el ícono de la fila.
const PLATFORM_CHANGE = {
    Component: PlatformChangeDetail,
    canRender: canRenderPlatformChange,
    // Sin un antes→después que mostrar, el panel repetía el título. Ver el docstring del predicado.
    hasDetail: platformChangeHasDetail,
};

// Las 4 acciones de curado comparten renderer; el título lo diferencia por event_type.
const MEMORY_CURATION_BURST = {
    Component: MemoryCurationBurstDetail,
    canRender: canRenderMemoryCurationBurst,
    getTitle: memoryCurationBurstTitle,
    hasDetail: memoryCurationBurstHasDetail,
};

export const BY_EVENT_TYPE = {
    ALERT_TRIGGERED: {Component: AlertDetail, canRender: isAlertShape, getTitle: alertTitle, hasDetail: alertHasDetail},

    BU_EDITED: {Component: FlatDiffDetail, canRender: isFlatDiffShape, getTitle: getFlatDiffTitle, hasDetail: flatDiffHasDetail},
    CLIENT_PROFILE_UPDATED: {Component: FlatDiffDetail, canRender: isFlatDiffShape, getTitle: getFlatDiffTitle, hasDetail: flatDiffHasDetail},
    CLIENT_INTEGRATIONS_UPDATED: {Component: FlatDiffDetail, canRender: isFlatDiffShape, getTitle: getFlatDiffTitle, hasDetail: flatDiffHasDetail},
    AD_ACCOUNT_EDITED: {Component: NestedDiffDetail, canRender: isNestedDiffShape, getTitle: getNestedDiffTitle, hasDetail: nestedDiffHasDetail},
    USER_EDITED: {Component: NestedDiffDetail, canRender: isNestedDiffShape, getTitle: getNestedDiffTitle, hasDetail: nestedDiffHasDetail},
    BUDGET_MODIFIED: {Component: BudgetModifiedDetail, canRender: isBeforeAfterShape},
    CAMPAIGN_BUDGET_MODIFIED: {Component: BudgetModifiedDetail, canRender: isBeforeAfterShape},
    ADGROUP_BUDGET_MODIFIED: {Component: BudgetModifiedDetail, canRender: isBeforeAfterShape},
    DAILY_LIMIT_EXCEEDED: {Component: DailyLimitDetail, canRender: d => typeof d?.kind === 'string'},

    PLATFORM_BUDGET_CHANGED: PLATFORM_CHANGE,
    PLATFORM_STATUS_CHANGED: PLATFORM_CHANGE,
    PLATFORM_BID_CHANGED: PLATFORM_CHANGE,
    PLATFORM_TARGETING_CHANGED: PLATFORM_CHANGE,
    PLATFORM_CREATIVE_CHANGED: PLATFORM_CHANGE,
    PLATFORM_STRUCTURE_CHANGED: PLATFORM_CHANGE,
    PLATFORM_SYSTEM_EVENT: PLATFORM_CHANGE,
    PLATFORM_OTHER: PLATFORM_CHANGE,

    MEDIA_PLAN_CHANGE_APPLIED: {Component: MediaPlanChangeDetail, canRender: canRenderMediaPlanChange},

    ITEM_COMMENT_UPDATED: {Component: SimpleFieldsDetail, canRender: canRenderCommentPreview},
    AGENT_SESSION_RENAMED: {Component: SimpleFieldsDetail, canRender: canRenderSessionRename},
    WEBHOOK_SENT: {Component: SimpleFieldsDetail, canRender: canRenderWebhookSent},
    USER_INVITED: {Component: SimpleFieldsDetail, canRender: canRenderInvitedEmail},
    USER_REMOVED: {Component: SimpleFieldsDetail, canRender: canRenderRemovedEmail},
    UNCONTROLLED_SPEND_FLAGGED: {Component: SimpleFieldsDetail, canRender: canRenderUncontrolledFlagged},

    // Curado de la Memoria: una operación suelta no trae detail y degrada a NO_DETAIL sola
    // (canRender falla); una tanda colapsada por el backend trae `collapsed_items` y se pinta
    // como "Se rechazaron 12 hechos" + la lista.
    MEMORY_FACT_REJECTED: MEMORY_CURATION_BURST,
    MEMORY_FACT_APPROVED: MEMORY_CURATION_BURST,
    MEMORY_FACT_SHARED: MEMORY_CURATION_BURST,
    MEMORY_FACT_EDITED: MEMORY_CURATION_BURST,
    MEMORY_FACT_CONFIRMED: MEMORY_CURATION_BURST,
    MEMORY_FACT_AUTO_ARCHIVED: MEMORY_CURATION_BURST,
    // Las fusiones del motor de duplicados (2026-09-12): una corrida sobre una biblioteca vieja
    // puede fusionar diez pares de un saque, y una persona resolviendo propuestas, varias seguidas.
    MEMORY_FACT_AUTO_MERGED: MEMORY_CURATION_BURST,
    MEMORY_FACT_MERGED: MEMORY_CURATION_BURST,
    MEMORY_FACT_RESTORED: MEMORY_CURATION_BURST,

    // Sin panel: el detalle no agrega nada sobre el título. El resto de los MEMORY_* tiene
    // detail vacío y se suprime solo.
    BU_CREATED: NO_DETAIL,
    BU_DELETED: NO_DETAIL,
    BU_ACTIVATED: NO_DETAIL,
    BU_DEACTIVATED: NO_DETAIL,
    BUCC_ADDED: NO_DETAIL,
    BUCC_EDITED: NO_DETAIL,
    BUCC_DELETED: NO_DETAIL,
    AD_ACCOUNT_LINKED: NO_DETAIL,
    AD_ACCOUNT_REMOVED: NO_DETAIL,
    CLIENT_ACTIVATED: NO_DETAIL,
    CLIENT_DEACTIVATED: NO_DETAIL,
    CLIENT_DELETED: NO_DETAIL,
    CLIENT_RESTORED: NO_DETAIL,
    REPORT_SAVED: NO_DETAIL,
    REPORT_DELETED: NO_DETAIL,
    SHEET_SYNC_CREATED: NO_DETAIL,
    SHEET_SYNC_DELETED: NO_DETAIL,
    SHEET_SYNC_UPDATED: NO_DETAIL,
    ALERTS_CONFIGURED: NO_DETAIL,
    COLUMNS_CONFIGURED: NO_DETAIL,
    USER_COMMENT: NO_DETAIL,
    AGENT_COMMENT: NO_DETAIL,
    AGENT_SESSION_CREATED: NO_DETAIL,
    AGENT_SESSION_USED: NO_DETAIL,
    MEETING_HELD: NO_DETAIL,
    DECISION_TAKEN: NO_DETAIL,
    COMMITMENT_MADE: NO_DETAIL,
    EMAIL_EXCHANGED: NO_DETAIL,
};

// Fallback por FORMA. El orden importa: las alertas van primero (tienen indicator/kind y
// pueden traer before/after sueltos), los diffs después, before/after al final.
// AlertDetail degrada solo a filas genéricas, así que también cubre payloads con forma de
// alerta que lleguen por un event_type sin registrar.
const BY_SHAPE = [
    {Component: AlertDetail, canRender: isAlertShape, hasDetail: alertHasDetail},
    {Component: NestedDiffDetail, canRender: isNestedDiffShape, hasDetail: nestedDiffHasDetail},
    {Component: FlatDiffDetail, canRender: isFlatDiffShape, hasDetail: flatDiffHasDetail},
    {Component: SimpleBeforeAfterDetail, canRender: isBeforeAfterShape},
];

const FALLBACK = {Component: GenericDetailTable, canRender: () => true};

function safeCanRender(descriptor, detail) {
    if (!descriptor.canRender) return true;
    try {
        return descriptor.canRender(detail);
    } catch (e) {
        return false;
    }
}

export function resolveRenderer(entry) {
    const detail = entry ? entry.detail : null;
    const byType = entry && BY_EVENT_TYPE[entry.event_type];
    if (byType && safeCanRender(byType, detail)) return byType;
    const byShape = BY_SHAPE.find(descriptor => safeCanRender(descriptor, detail));
    return byShape || FALLBACK;
}

// ¿La entrada tiene algo que valga la pena expandir? Gobierna si la fila muestra
// "Ver detalle": un detail vacío o solo con campos técnicos no genera toggle.
export function hasRenderableDetail(entry) {
    const detail = entry ? entry.detail : null;
    if (!isPlainObject(detail)) return false;
    const descriptor = resolveRenderer(entry);
    if (!descriptor.Component) return false;
    if (descriptor.hasDetail) {
        try {
            return !!descriptor.hasDetail(detail);
        } catch (e) {
            return false;
        }
    }
    // Default: ¿el render genérico produciría al menos una fila visible? (los objetos
    // irreducibles, arrays vacíos y campos técnicos no cuentan).
    return hasRenderableRows(detail);
}

// Título de la fila. Los descriptors pueden componer un título más rico (getTitle);
// si no hay o devuelve null, cae a la description del backend, limpiada.
export function resolveTitle(entry, t) {
    if (!entry) return '';
    const descriptor = BY_EVENT_TYPE[entry.event_type];
    if (descriptor && descriptor.getTitle) {
        let composed = null;
        try {
            composed = descriptor.getTitle(entry, t);
        } catch (e) {
            composed = null;
        }
        if (composed) return withCurrentLineTerm(composed);
    }
    return withCurrentLineTerm(cleanDescription(entry, t));
}

// La description es la que armó el backend AL MOMENTO del evento, y los eventos de línea anteriores
// al renombre (card mFHzKeNK, 27/09) dicen «Línea de control 'X' creada/editada/desactivada…». Esas
// filas no se migran: el título las muestra con el nombre de hoy, igual que las nuevas. Anclado al
// comienzo y a la comilla del nombre, que es la forma exacta de esos eventos (`activity_log_service.py`
// y el alta desde el media plan): un nombre de línea o de cuenta que contenga las palabras no se toca.
const LEGACY_LINE_PREFIX = /^L[ií]nea de control (?=')/;

function withCurrentLineTerm(title) {
    return typeof title === 'string' ? title.replace(LEGACY_LINE_PREFIX, 'Línea de seguimiento ') : title;
}

// El mapper de platform changes antepone la plataforma a la descripción ("Meta · ...") y
// el badge de canal de la fila ya la dice; y la description de una alerta termina con el
// identificador crudo del indicador — se muestra con su nombre legible (INDICATOR_LABELS
// cubre el inventario completo; VALUE_LABELS queda como red para valores históricos).
function cleanDescription(entry, t) {
    // description es texto del backend, pero un payload corrupto no puede tumbar la fila.
    let description = String(entry.description || '').replace(/^(Meta|Google(?: Ads)?) · /, '');
    const indicator = entry.category === 'ALERTS' && entry.detail && entry.detail.indicator;
    const indicatorLabel = indicator && (INDICATOR_LABELS[indicator] || VALUE_LABELS[indicator]);
    if (indicatorLabel) {
        description = description.replace(indicator, t(indicatorLabel));
    }
    return description;
}
